import { defineResource } from '@tnn/core'
import { z } from 'zod'
import { signingField } from '../signature'

/** `partial`: payments (accounting.payment) settled part of it; `paid`: all of it */
export const invoiceStatuses = ['draft', 'issued', 'partial', 'paid', 'cancelled'] as const
export type InvoiceStatus = (typeof invoiceStatuses)[number]

export const invoiceLineSchema = z.object({
  description: z.string().trim().min(1).max(300),
  qty: z.number().min(0),
  unitPrice: z.number().min(0),
})
export type InvoiceLine = z.infer<typeof invoiceLineSchema>

export const invoice = defineResource({
  app: 'accounting',
  name: 'invoice',
  path: 'invoices',
  schema: z.object({
    customerId: z.string().min(1),
    issueDate: z.iso.date(),
    dueDate: z.iso.date().nullish(),
    lines: z.array(invoiceLineSchema).min(1),
    /** None by default: freight within Thailand is exempt from VAT (ประมวลรัษฎากร ม.81(1)(ณ)); 7 for what is not */
    vatRate: z.number().min(0).max(100).default(0),
    /** Withholding tax deducted by the customer; transport services are 1%; never for an individual (`canWithhold`) */
    whtRate: z.number().min(0).max(100).default(1),
    notes: z.string().max(1000).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 140, priority: 1 } },
    customerId: {
      type: 'ref',
      ref: { resource: 'accounting.customer', where: { status: 'active', isCustomer: 'true' } },
      filter: true,
      search: true,
      list: { priority: 1 },
      // Warns of what the printed document will go without (PartnerField)
      form: { span: 6, custom: true },
    },
    issueDate: { filter: true, list: { width: 130, priority: 2 }, form: { span: 3 } },
    dueDate: { list: { width: 130, priority: 3 }, form: { span: 3 } },
    lines: { form: { span: 12, section: 'lines', custom: true }, import: false },
    vatRate: { type: 'percent', list: false, form: { span: 3, section: 'totals' } },
    // Drawn by the app: 0 and fixed for an individual customer (`canWithhold`)
    whtRate: { type: 'percent', list: false, form: { span: 3, section: 'totals', custom: true } },
    notes: { type: 'textarea', form: { span: 12, section: 'other' } },
    status: {
      readOnly: true,
      type: 'select',
      options: invoiceStatuses,
      filter: true,
      // Not paid yet amber and paid in part as a waybill's or job's; each its own number on the menu, in its colour
      tones: { draft: 'neutral', issued: 'warning', partial: 'progress', paid: 'success', cancelled: 'danger' },
      list: { width: 130, priority: 1 },
    },
    subtotal: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    vat: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    total: { readOnly: true, type: 'money', list: { align: 'end', width: 140, priority: 1 } },
    /** Settled by payments so far: money, withholding tax and amounts written off (server-managed) */
    paidAmount: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    /** Taken off by credit notes (accounting.creditNote) */
    credited: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    /** Still to be paid: total less credited and paidAmount */
    outstanding: { readOnly: true, type: 'money', list: { align: 'end', width: 140, priority: 2 } },
    /** When it became fully paid: the date of the payment that settled the last of it */
    paidAt: { readOnly: true, type: 'datetime', list: { hidden: true } },
    /** The billing note it was handed over on (one at a time) */
    billingNoteId: {
      readOnly: true,
      type: 'ref',
      ref: { resource: 'accounting.billingNote' },
      filter: true,
      list: { priority: 3, hidden: true },
    },
    /** Brought forward from the old program (server-set): its old number, no tax invoice issued here */
    opening: { readOnly: true, type: 'boolean', filter: true, list: { width: 100, priority: 3, hidden: true } },
    /** Submitted payments that settled (part of) it */
    paymentIds: { readOnly: true, type: 'multiselect', ref: { resource: 'accounting.payment' }, list: false },
    /** The customer's line: signed for, a link waiting, or not yet (server-set) */
    signing: signingField,
  },
  title: 'number',
  columns: [
    'number',
    'customerId',
    'issueDate',
    'dueDate',
    'total',
    'outstanding',
    'status',
    'signing',
    'subtotal',
    'vat',
    'paidAmount',
    'paidAt',
  ],
  sections: ['main', 'lines', 'totals', 'other'],
  defaultSort: '-issueDate',
  actions: ['read', 'create', 'update', 'delete', 'export', 'print'],
  // Submitting issues the tax invoice (numbered then); one with payments on it cannot be cancelled (cancel them first)
  submittable: { cancelReason: 'required' },
  card: { title: 'customerId', subtitle: 'number', meta: ['issueDate', 'outstanding'], status: 'status' },
})

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

export interface InvoiceTotals {
  subtotal: number
  vat: number
  wht: number
  total: number
  /** What the customer actually pays after withholding tax */
  net: number
}

/** Same calculation on the form (live) and the API (stored). */
export function invoiceTotals(lines: readonly Pick<InvoiceLine, 'qty' | 'unitPrice'>[], vatRate = 0, whtRate = 0): InvoiceTotals {
  const subtotal = round2(lines.reduce((sum, l) => sum + (l.qty || 0) * (l.unitPrice || 0), 0))
  const vat = round2((subtotal * vatRate) / 100)
  const wht = round2((subtotal * whtRate) / 100)
  const total = round2(subtotal + vat)
  return { subtotal, vat, wht, total, net: round2(total - wht) }
}

/** Withholding tax the customer may deduct on `amount` of an invoice: its share of the invoice's tax (on the subtotal) */
export function whtOn(invoice: { subtotal?: number | null; total?: number | null; whtRate?: number | null }, amount: number): number {
  const total = Number(invoice.total) || 0
  if (total <= 0) return 0
  const wht = ((Number(invoice.subtotal) || 0) * (Number(invoice.whtRate) || 0)) / 100
  return round2((wht * Math.min(amount, total)) / total)
}
